import React, { useEffect, useState } from 'react';
import './App.css';

const LS_KEYS = {
  apiKey: 'pf_apiKey',
  count: 'pf_count',
  topics: 'pf_topics',
  players: 'pf_players',
  questions: 'pf_questions',
  promptSuffix: 'pf_promptSuffix',
};

const DEFAULT_PROMPT_SUFFIX = `The questions and the answers should be in Hungarian. The answer should not be a number. It should be a subject, obejct, person, or other noun phrase. The fun fact should be something that makes people laugh or be surprised when they hear the answer. "What did ancient Egyptians do when their cat died?" (Answer: They shaved their eyebrows) or "In 2018, a man in Australia tried to sue his boss for $1.8 million for doing what?" (Answer: For giving him a bad desk). These questions involve bizarre, real-life facts or absurd scenarios that sound unbelievable, testing creativity and bluffing skills. The sentences should be gramatically correct and well-structured. The fun facts should most importantly say the truth.`;

function readJson(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch (e) {
    return fallback;
  }
}

function App() {
  const [apiKey, setApiKey] = useState(localStorage.getItem(LS_KEYS.apiKey) || '');
  const [count, setCount] = useState(readJson(LS_KEYS.count, 5));
  const [topics, setTopics] = useState(readJson(LS_KEYS.topics, ['Science', 'History']));
  const [players, setPlayers] = useState(readJson(LS_KEYS.players, ['Alice', 'Bob']));
  const [questions, setQuestions] = useState(readJson(LS_KEYS.questions, []));
  const [editing, setEditing] = useState({});
  const [visibleAnswers, setVisibleAnswers] = useState({});
  const [promptSuffix, setPromptSuffix] = useState(readJson(LS_KEYS.promptSuffix, DEFAULT_PROMPT_SUFFIX));
  const [loading, setLoading] = useState(false);
  const [route, setRoute] = useState(window.location.pathname || '/');

  useEffect(() => {
    localStorage.setItem(LS_KEYS.apiKey, apiKey);
  }, [apiKey]);

  useEffect(() => {
    localStorage.setItem(LS_KEYS.count, JSON.stringify(count));
  }, [count]);

  useEffect(() => {
    localStorage.setItem(LS_KEYS.topics, JSON.stringify(topics));
  }, [topics]);

  useEffect(() => {
    localStorage.setItem(LS_KEYS.players, JSON.stringify(players));
  }, [players]);

  useEffect(() => {
    localStorage.setItem(LS_KEYS.questions, JSON.stringify(questions));
  }, [questions]);

  useEffect(() => {
    localStorage.setItem(LS_KEYS.promptSuffix, JSON.stringify(promptSuffix));
  }, [promptSuffix]);

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname || '/');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function navigate(pathname, search = '') {
    const url = pathname + (search || '');
    window.history.pushState({}, '', url);
    setRoute(pathname);
  }

  function addListItem(setter, list, value = '') {
    setter([...list, value]);
  }

  function updateListItem(setter, list, idx, value) {
    const copy = [...list];
    copy[idx] = value;
    setter(copy);
  }

  function deleteListItem(setter, list, idx) {
    const copy = [...list];
    copy.splice(idx, 1);
    setter(copy);
  }

  function toggleAnswer(idx) {
    setVisibleAnswers((s) => ({ ...s, [idx]: !s[idx] }));
  }

  useEffect(() => {
    // reset visibility when list length changes (add/delete)
    setVisibleAnswers({});
  }, [questions.length]);

  async function generateQuestions() {
    if (!apiKey) return alert('Please enter your OpenAI API key.');
    setLoading(true);
    try {
      const base = `You are asked to generate ${count} fun-fact style quiz prompts based on these topics: ${topics.join(', ')}. For each item, produce a single-sentence fun fact where the "fun part" (the surprising fact) is removed and replaced with a blank (use "_____"), and provide the missing piece as the answer. Return a JSON array of objects like [{"question":"...","answer":"..."}]. Keep answers short.`;
      const prompt = base + "\n\n" + promptSuffix;

      const body = {
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: 'You generate quiz-style fun facts with blanked answers and a compact JSON output.' },
          { role: 'user', content: prompt },
        ],
        max_tokens: 4000,
        temperature: 0.8,
      };

      const resp = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || resp.statusText);
      }

      const data = await resp.json();
      const text = data.choices?.[0]?.message?.content || data.choices?.[0]?.text || '';

      let parsed = null;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        // try to extract JSON block
        const m = text.match(/\[\s*\{[\s\S]*\}\s*\]/);
        if (m) parsed = JSON.parse(m[0]);
      }

      if (!parsed || !Array.isArray(parsed)) {
        throw new Error('Could not parse assistant response as JSON array. Response:\n' + text);
      }

      // normalize entries
      const newQs = parsed.slice(0, count).map((it) => ({ question: it.question || it.q || '', answer: it.answer || it.a || '' }));
      setQuestions((prev) => [...prev, ...newQs]);
    } catch (err) {
      console.error(err);
      alert('Error generating questions: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  }

  function clearAllQuestions() {
    const ok = window.confirm('Delete all questions? This action cannot be undone.');
    if (!ok) return;
    setQuestions([]);
    setEditing({});
    setVisibleAnswers({});
  }

  // Play view component (simple, inline)
  function PlayView() {
    const params = new URLSearchParams(window.location.search);
    const rawIndex = parseInt(params.get('i') || '0', 10);
    const idx = isNaN(rawIndex) ? 0 : rawIndex;
    const qCount = questions.length;
    const [visible, setVisible] = useState(false);
    const [currentIdx, setCurrentIdx] = useState(idx);
    const [currentPlayer, setCurrentPlayer] = useState(null);

    useEffect(() => {
      // sync param -> state when user navigates with history
      const onPop = () => {
        const p = new URLSearchParams(window.location.search);
        setCurrentIdx(Number(p.get('i') || 0));
      };
      window.addEventListener('popstate', onPop);
      return () => window.removeEventListener('popstate', onPop);
    }, []);

    function goTo(newIdx) {
      const search = '?i=' + newIdx;
      window.history.pushState({}, '', '/play' + search);
      setCurrentIdx(newIdx);
    }

    function prev() {
      goTo(currentIdx - 1);
      setVisible(false);
      setCurrentPlayer(null);
    }

    function next() {
      goTo(currentIdx + 1);
      setVisible(false);
      setCurrentPlayer(null);
    }

    function unhide() {
      const ok = window.confirm('Reveal the answer? Confirm to show the answer and pick a random player.');
      if (!ok) return;
      setVisible(true);
      if (players && players.length > 0) {
        const roundOwner = players[effectiveIdx % players.length];
        const candidates = players.filter((pl) => pl !== roundOwner);
        if (candidates.length === 0) {
          // no other players to pick
          alert('No other players available to pick.');
          setCurrentPlayer(null);
        } else {
          const p = candidates[Math.floor(Math.random() * candidates.length)];
          setCurrentPlayer(p);
        }
      }
    }

    function hide() {
      setVisible(false);
      setCurrentPlayer(null);
    }

    if (qCount === 0) {
      return (
        <div className="container my-4">
          <div className="alert alert-warning">No questions available. Go back and generate some.</div>
          <button className="btn btn-secondary" onClick={() => navigate('/')}>Back</button>
        </div>
      );
    }

    const effectiveIdx = ((currentIdx % qCount) + qCount) % qCount; // modulo safe
    const q = questions[effectiveIdx];

    return (
      <div className="container my-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-secondary" onClick={() => navigate('/')}>Back</button>
          <div>Round: {players && players.length > 0 ? players[effectiveIdx % players.length] : '—'}</div>
          <div className="badge bg-secondary">{effectiveIdx + 1}/{qCount}</div>
        </div>

        <div className="card text-center">
          <div className="card-body">
            <h1 className="card-title display-6">{q.question}</h1>
            <p className="lead mt-3">{visible ? <strong>{q.answer}</strong> : <span className="text-muted">Answer hidden</span>}</p>
            {visible && currentPlayer && <div className="mb-2">Selected player: <strong>{currentPlayer}</strong></div>}

            <div className="d-flex justify-content-center gap-2 mt-3">
              <button className="btn btn-outline-primary" onClick={prev}>Previous</button>
              {visible ? (
                <button className="btn btn-outline-secondary" onClick={hide}>Hide</button>
              ) : (
                <button className="btn btn-primary" onClick={unhide}>Unhide (confirm)</button>
              )}
              <button className="btn btn-outline-primary" onClick={next}>Next</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (route === '/play') {
    return <PlayView />;
  }

  return (
    <div className="App container my-4">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="card-title mb-0">Question Generator</h2>
            <button type="button" className="btn btn-success" onClick={() => navigate('/play', '?i=0')} disabled={questions.length === 0}>Start</button>
          </div>

          <div className="row g-3 align-items-center mb-3">
            <div className="col-md-8">
              <label className="form-label">OpenAI API Key</label>
              <input type="password" className="form-control" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Number of questions</label>
              <input type="number" min={1} max={100} className="form-control" value={count} onChange={(e) => setCount(Number(e.target.value || 0))} />
            </div>
          </div>

          <div className="row">
            <div className="col-md-3">
              <h5>Topics</h5>
              <ul className="list-group mb-2">
                {topics.map((t, i) => (
                  <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                    {editing[`topics_${i}`] ? (
                      <div className="w-100 d-flex gap-2">
                        <input className="form-control form-control-sm" value={t} onChange={(e) => updateListItem(setTopics, topics, i, e.target.value)} />
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditing((s) => ({ ...s, [`topics_${i}`]: false }))}>Save</button>
                      </div>
                    ) : (
                      <>
                        <span>{t}</span>
                        <div>
                          <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => setEditing((s) => ({ ...s, [`topics_${i}`]: true }))}>Edit</button>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteListItem(setTopics, topics, i)}>Delete</button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <button type="button" className="btn btn-sm btn-success" onClick={() => addListItem(setTopics, topics, 'New Topic')}>Add Topic</button>
            </div>

            <div className="col-md-3">
              <h5>Players</h5>
              <ul className="list-group mb-2">
                {players.map((p, i) => (
                  <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                    {editing[`players_${i}`] ? (
                      <div className="w-100 d-flex gap-2">
                        <input className="form-control form-control-sm" value={p} onChange={(e) => updateListItem(setPlayers, players, i, e.target.value)} />
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditing((s) => ({ ...s, [`players_${i}`]: false }))}>Save</button>
                      </div>
                    ) : (
                      <>
                        <span>{p}</span>
                        <div>
                          <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => setEditing((s) => ({ ...s, [`players_${i}`]: true }))}>Edit</button>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteListItem(setPlayers, players, i)}>Delete</button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <button type="button" className="btn btn-sm btn-success" onClick={() => addListItem(setPlayers, players, 'New Player')}>Add Player</button>
            </div>

            <div className="col-md-6">
              <h5>Questions</h5>
              <ul className="list-group mb-2">
                {questions.map((q, i) => (
                  <li key={i} className="list-group-item d-flex justify-content-between align-items-start">
                    <div className="ms-2 me-auto">
                      {editing[`questions_${i}`] ? (
                        <div className="d-flex gap-2">
                          <input className="form-control form-control-sm" value={q.question} onChange={(e) => updateListItem(setQuestions, questions, i, { ...q, question: e.target.value })} />
                          <input className="form-control form-control-sm" value={q.answer} onChange={(e) => updateListItem(setQuestions, questions, i, { ...q, answer: e.target.value })} />
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditing((s) => ({ ...s, [`questions_${i}`]: false }))}>Save</button>
                        </div>
                      ) : (
                        <div>
                          <div className="fw-bold">{q.question}</div>
                          <div className="text-muted">Answer: {visibleAnswers[i] ? <span>{q.answer}</span> : <span>••••••</span>}</div>
                        </div>
                      )}
                    </div>
                    {!editing[`questions_${i}`] && (
                      <div className="btn-group-vertical">
                        <button type="button" className="btn btn-sm btn-outline-info mb-1" onClick={() => toggleAnswer(i)}>{visibleAnswers[i] ? 'Hide' : 'Show'}</button>
                        <button type="button" className="btn btn-sm btn-outline-secondary mb-1" onClick={() => setEditing((s) => ({ ...s, [`questions_${i}`]: true }))}>Edit</button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteListItem(setQuestions, questions, i)}>Delete</button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <button type="button" className="btn btn-sm btn-success" onClick={() => setQuestions((s) => [...s, { question: 'New blank _____', answer: 'answer' }])}>Add Question</button>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Prompt suffix (appended to generation prompt)</label>
            <textarea className="form-control" rows={5} value={promptSuffix} onChange={(e) => setPromptSuffix(e.target.value)} />
          </div>

          <div className="mt-3 d-flex align-items-center">
            <button type="button" className="btn btn-primary" onClick={generateQuestions} disabled={loading}>{loading ? 'Generating…' : 'Generate questions'}</button>
            <button type="button" className="btn btn-danger ms-2" onClick={clearAllQuestions} disabled={questions.length === 0}>Delete all questions</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
